import * as THREE from 'three';
import { isBoss, type EnemyClass, type SimEvent } from '@orb/sim';
import type { SimView } from '../game/Driver';
import type { Horde } from '../game/Horde';

/**
 * The bosses' health over their heads (HUD): a bronze-edged bar in the boss's colour, the armour's
 * cracks marked at a half and a quarter, the name under it; and as a boss walks out of the fog a line
 * under the gate's plate says so. Projected each frame like the totem's label; written only when the
 * position or the health changed. Bosses only (a few at once): soldiers stay bare. A surge (a pack out of
 * the fog at once) is said on the same line.
 */
const NAME: Partial<Record<EnemyClass, string>> = { brute: 'Громило', herald: 'Глашатай', pitboss: 'Володар ями' };
const COLOR: Partial<Record<EnemyClass, string>> = { brute: '#ff7a3a', herald: '#f0c060', pitboss: '#af73ff' };
const _p = new THREE.Vector3();

interface Bar { el: HTMLDivElement; fill: HTMLElement; key: string; announced: boolean }

export class BossBars implements SimView {
  private readonly layer: HTMLDivElement;
  private readonly banner: HTMLDivElement;
  private readonly bars = new Map<number, Bar>();
  private bannerTimer = 0;
  private width = innerWidth; private height = innerHeight;

  constructor(host: HTMLElement, private readonly horde: Horde) {
    this.layer = document.createElement('div');
    this.layer.className = 'boss-bars';
    this.layer.setAttribute('aria-hidden', 'true');
    this.banner = document.createElement('div');
    this.banner.className = 'boss-banner';
    this.banner.setAttribute('role', 'status');
    this.banner.hidden = true;
    host.append(this.layer, this.banner);
    const size = () => { this.width = host.clientWidth || innerWidth; this.height = host.clientHeight || innerHeight; };
    size(); new ResizeObserver(size).observe(host);
  }

  update(camera: THREE.Camera) {
    const seen = new Set<number>();
    for (const v of this.horde.enemies) {
      if (!isBoss(v.cls) || v.dying > 0 || v.leaked || !v.e.alive || v.reveal < 0.85) continue;
      seen.add(v.id);
      let bar = this.bars.get(v.id);
      if (!bar) {
        const el = document.createElement('div');
        el.className = 'boss-bar';
        el.style.setProperty('--boss', COLOR[v.cls] ?? '#ff7a3a');
        el.innerHTML = '<span class="boss-bar-track"><b></b><i style="left:50%"></i><i style="left:25%"></i></span><span class="boss-bar-name"></span>';
        el.querySelector('.boss-bar-name')!.textContent = NAME[v.cls] ?? 'Бос';
        bar = { el, fill: el.querySelector('b')!, key: '', announced: false };
        this.bars.set(v.id, bar);
        this.layer.append(el);
      }
      if (!bar.announced) { bar.announced = true; this.announce(`${NAME[v.cls] ?? 'Бос'} виходить з туману`); }
      // over the head: the boss's height (its scale) and the phone's taller warriors
      _p.set(v.pos.x, 2.05 * v.e.scale * this.horde.drawScale, v.pos.z).project(camera);
      // kept inside the screen: a boss just out of the fog stands at its very edge
      const x = Math.round(Math.min(this.width - 46, Math.max(46, (_p.x + 1) * this.width / 2))), y = Math.round(Math.max(40, (1 - _p.y) * this.height / 2));
      const k = Math.max(0, v.e.hp / v.e.maxHp), key = `${x},${y},${Math.round(k * 200)}`;
      if (key === bar.key) continue;
      bar.key = key;
      bar.el.style.transform = `translate(${x}px, ${y}px) translate(-50%, -100%)`;
      bar.fill.style.transform = `scaleX(${k})`;
      bar.el.toggleAttribute('data-low', k < 0.25);
    }
    for (const [id, bar] of this.bars) if (!seen.has(id)) { bar.el.remove(); this.bars.delete(id); }
  }

  events(events: readonly SimEvent[]) {
    for (const e of events) if (e.type === 'surge') this.announce(e.runners ? `Сплеск орди · ${e.size} з туману, з бігунами` : `Сплеск орди · ${e.size} з туману`);
  }

  /** A line under the gate's plate for a couple of seconds. */
  private announce(text: string) {
    this.banner.textContent = text;
    this.banner.hidden = false;
    this.banner.classList.remove('in'); void this.banner.offsetWidth; this.banner.classList.add('in');
    clearTimeout(this.bannerTimer);
    this.bannerTimer = window.setTimeout(() => { this.banner.hidden = true; }, 2600);
  }
}
